import { useEffect, useState } from 'react';
import { subscribeToTechnicals } from '../firebase';
import { ALIAS_DICTIONARY } from '../utils/symbolAlias';


/**
 * useTechnicals — subscribes to the global technicals document and returns
 * the indicators map { [symbol]: { rsi, pivot, r1, r2, r3, s1, s2, s3, ma50, ma200, w52High, w52Low, fib, lastClose, updatedAt } }
 *
 * Updated hourly by the updateTechnicalIndicators cloud function.
 * All clients share the same data — zero extra API calls from frontend.
 */
export const useTechnicals = () => {
  const [technicals, setTechnicals] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const unsubscribe = subscribeToTechnicals((data) => {
      if (!isMounted || !data) return;
      // Separate metadata from per-symbol entries
      const { _updatedAt, _forceRunAt, _finalizationSessionDate, ...symbols } = data;
      // Parse JSON-stringified arrays (spark, lastBars, fib) back to objects.
      // These are stored as strings in Firestore to stay under the 40k index entry limit.
      for (const sym of Object.keys(symbols)) {
        const s = symbols[sym];
        if (!s || typeof s !== 'object') continue;
        try { if (typeof s.spark === 'string') s.spark = JSON.parse(s.spark); } catch {}
        try { if (typeof s.lastBars === 'string') s.lastBars = JSON.parse(s.lastBars); } catch {}
        try { if (typeof s.fib === 'string') s.fib = JSON.parse(s.fib); } catch {}
        try { if (typeof s.splits === 'string') s.splits = JSON.parse(s.splits); } catch {}
      }

      // Inject aliases so the UI can look up by alias natively
      for (const [alias, canonical] of Object.entries(ALIAS_DICTIONARY)) {
        if (symbols[canonical]) {
          symbols[alias] = symbols[canonical];
        }
      }

      setTechnicals(symbols);
      setUpdatedAt(_updatedAt || null);
    });
    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return { technicals, updatedAt };
};

export default useTechnicals;
